import { useState } from 'react';
import { App } from '@back4app2/sdk';
import back4app2 from '../back4app2';
import usePaddleCheckout from '../hooks/usePaddleCheckout';
import { DsButton } from './ds';
import { CardIcon } from './ds/icons';

const FRAME_TARGET = 'card-update-checkout';

/** Paddle flips the subscription back to active a moment after the card pays. */
const REFRESH_ATTEMPTS = 3;
const REFRESH_DELAY_MS = 2000;

/**
 * The renewal is failing, and here is where the card is replaced.
 *
 * Paddle keeps retrying a failed renewal for a while before it cancels the
 * subscription, and the app keeps running and deploying the whole time. So
 * this is a warning, not a block — and the warning is only worth showing next
 * to the one thing that fixes it.
 *
 * The button opens Paddle's own card-update transaction for this app's
 * subscription. When it completes, the app re-reads its subscription instead
 * of waiting for the webhook, so the notice clears while the customer is still
 * looking at it.
 *
 * Same box and place as `TemporaryUrlNotice`. The warning colour is the
 * hairline and the icon only, and the body is one sentence.
 */
const PaymentPastDueNotice = (props: { app: App; onUpdated?: () => void }) =>
  // The check sits outside the card so Paddle is only loaded on the rare app
  // that needs it, not on every app page anyone opens.
  props.app.mainService?.mainServiceEnvironment?.paymentPastDueAt ? (
    <PastDueCard {...props} />
  ) : null;

const PastDueCard = ({
  app,
  onUpdated,
}: {
  app: App;
  onUpdated?: () => void;
}) => {
  // Lazy: this card can share a page with another checkout, and Paddle keeps
  // one event callback per page — it is taken on click, not on render.
  const { configured, openCheckout, closeCheckout } = usePaddleCheckout({
    lazy: true,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const settleAfterPayment = async () => {
    setIsOpen(false);
    setIsWorking(true);

    for (let attempt = 0; attempt < REFRESH_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, REFRESH_DELAY_MS));
      }

      try {
        await back4app2.refreshContainerAppSubscription(app.id);

        const fresh = await back4app2.findAppById(app.id);

        if (!fresh?.mainService?.mainServiceEnvironment?.paymentPastDueAt) {
          break;
        }
      } catch (e) {
        console.error('could not refresh the subscription', e);
      }
    }

    setIsWorking(false);
    onUpdated?.();
  };

  const onUpdateCard = async () => {
    setErrorMessage('');
    setIsWorking(true);

    let transactionId: string;

    try {
      transactionId = await back4app2.containerPaymentMethodChangeTransaction(
        app.id
      );
    } catch (e) {
      console.error('could not start the card update', e);
      setErrorMessage("We couldn't open the card update. Try again in a minute.");
      setIsWorking(false);

      return;
    }

    setIsWorking(false);
    setIsOpen(true);

    // Paddle draws into a node that has to exist already, and the state
    // change above is what creates it.
    window.setTimeout(() => {
      openCheckout({
        transactionId,
        frameTarget: FRAME_TARGET,
        onCompleted: () => {
          settleAfterPayment();
        },
        onClosed: () => setIsOpen(false),
        tracking: { source: 'past_due_card_update', app_id: app.id },
      });
    }, 200);
  };

  return (
    <>
      <div className="w-full p-3 rounded-card border border-state-warn/50 bg-navy-700 flex flex-col gap-2.5">
        <div className="flex items-start gap-2">
          <CardIcon size={16} className="flex-none mt-0.5 text-state-warn" />
          <div className="flex flex-col gap-1 min-w-0">
            <span className="font-sora text-ds-135 font-semibold tracking-[-0.01em] text-white">
              Payment failed
            </span>
            <p className="m-0 text-ds-115 leading-[1.5] text-navy-200">
              We couldn't renew this app's plan. It stays online while we retry,
              so update your card before the plan is canceled.
            </p>
          </div>
        </div>
        {errorMessage ? (
          <p className="m-0 text-ds-115 leading-[1.5] text-navy-200">{errorMessage}</p>
        ) : null}
        <DsButton
          variant="outline"
          size="lg"
          className="w-full"
          isDisabled={isWorking || !configured}
          onClick={onUpdateCard}
        >
          {isWorking ? 'Working…' : 'Update card'}
        </DsButton>
      </div>

      {isOpen ? (
        <>
          <div className="fixed inset-0 bg-navy-950/60 backdrop-blur-md z-[10000]" />
          <div className="fixed inset-0 z-[10001] flex items-start justify-center overflow-y-auto p-4 lg:p-8">
            <div className="relative w-full max-w-[520px] my-auto bg-navy-800 border border-navy-400 rounded-card shadow-[0_14px_48px_rgba(0,0,0,0.5)] overflow-hidden p-4 lg:p-6">
              <button
                type="button"
                onClick={() => {
                  // Tear down Paddle's frame, then close our own box: inline,
                  // `checkout.closed` does not arrive for a close we started.
                  closeCheckout();
                  setIsOpen(false);
                }}
                aria-label="Close card update"
                className="b4-focus absolute top-3 right-3 z-10 h-8 w-8 rounded-control inline-flex items-center justify-center text-navy-200 hover:text-white hover:bg-navy-600 transition-colors duration-[120ms]"
              >
                ✕
              </button>
              <div className={`${FRAME_TARGET} w-full min-h-[450px]`} />
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default PaymentPastDueNotice;
